import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import HubHero from "@/components/HubHero/HubHero";
import permissionsService from "@/services/permissionsService";
import { useHubDashboard } from "./hooks/useHubDashboard";
import { MinimalResourceCard } from "./components/MinimalResourceCard";
import { SharedResourceCard } from "./components/SharedResourceCard";
import { format } from "date-fns";

const HubDashboard: React.FC = () => {
  const { news, featuredResources, favorites, sharedWithMe, loading, refetch } =
    useHubDashboard();
  const { hasPermission } = permissionsService();
  const hasResourcesPermission = hasPermission("resources");

  return (
    <div className="relative min-h-full bg-gray-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 dark:bg-gradient-to-b">
      {/* Radial glow accents - extend across entire page */}
      <div className="absolute top-32 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-gradient-radial from-slate-300/30 via-transparent to-transparent dark:from-slate-600/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-0 right-0 w-[500px] h-[400px] bg-gradient-radial from-blue-400/10 via-transparent to-transparent dark:from-blue-500/10 rounded-full blur-3xl pointer-events-none" />

      {/* Hero Section - Full Width */}
      <HubHero />

      {/* Content Sections - Responsive width: tighter on tablet, wider on desktop */}
      {hasResourcesPermission && (
        <div className="relative mx-auto max-w-5xl lg:max-w-6xl xl:max-w-7xl px-4 sm:px-6 lg:px-8 pt-10 md:pt-14 pb-16">
          <div className="space-y-12">
            {/* Featured News Section */}
            {news && news.filter((item) => item.isFeatured).length > 0 && (
              <section>
                <div className="mb-6 flex items-center justify-between">
                  <h2 className="text-base font-normal text-gray-900 dark:text-white">
                    Featured
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-6">
                  {news
                    .filter((item) => item.isFeatured)
                    .map((item) => (
                      <Link
                        className="group block hover:no-underline"
                        key={item.id}
                        to={`/hub/resources/${item.slug}`}
                      >
                        <div className="h-full">
                          {item.thumbnailUrl && (
                            <div className="w-full overflow-hidden rounded-lg mb-3">
                              <img
                                alt={item.title}
                                className="block w-full h-auto transition-transform duration-300 group-hover:scale-105"
                                src={item.thumbnailUrl}
                              />
                            </div>
                          )}
                          <div className="space-y-1">
                            {item.createdOrPublishStartDate && (
                              <p className="text-xs text-gray-400 dark:text-gray-500 font-light">
                                {format(
                                  new Date(item.createdOrPublishStartDate),
                                  "MMM d, yyyy",
                                )}
                              </p>
                            )}
                            <h3 className="text-sm font-normal line-clamp-2 text-gray-700 dark:text-gray-200 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                              {item.title}
                            </h3>
                          </div>
                        </div>
                      </Link>
                    ))}
                </div>
              </section>
            )}

            {/* Recently Added Section */}
            {news && news.filter((item) => !item.isFeatured).length > 0 && (
              <section className="pt-10 border-t border-gray-200 dark:border-slate-700/50">
                <div className="mb-6 flex items-center justify-between">
                  <h2 className="text-base font-normal text-gray-900 dark:text-white">
                    News
                  </h2>
                  <Link
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-sm font-light inline-flex items-center gap-1 transition-colors"
                    to="/hub/news"
                  >
                    View All
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-6">
                  {news
                    .filter((item) => !item.isFeatured)
                    .slice(0, 3)
                    .map((item) => (
                      <Link
                        className="group block hover:no-underline"
                        key={item.id}
                        to={`/hub/resources/${item.slug}`}
                      >
                        <div className="h-full">
                          {item.thumbnailUrl && (
                            <div className="w-full overflow-hidden rounded-lg mb-3">
                              <img
                                alt={item.title}
                                className="block w-full h-auto transition-transform duration-300 group-hover:scale-105"
                                src={item.thumbnailUrl}
                              />
                            </div>
                          )}
                          <div className="space-y-1">
                            {item.createdOrPublishStartDate && (
                              <p className="text-xs text-gray-400 dark:text-gray-500 font-light">
                                {format(
                                  new Date(item.createdOrPublishStartDate),
                                  "MMM d, yyyy",
                                )}
                              </p>
                            )}
                            <h3 className="text-sm font-normal line-clamp-2 text-gray-700 dark:text-gray-200 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                              {item.title}
                            </h3>
                          </div>
                        </div>
                      </Link>
                    ))}
                </div>
              </section>
            )}

            {/* Featured Resources Section */}
            {featuredResources && featuredResources.length > 0 && (
              <section className="pt-10 border-t border-gray-200 dark:border-slate-700/50">
                <div className="mb-6 flex items-center justify-between">
                  <h2 className="text-base font-normal text-gray-900 dark:text-white">
                    Resources
                  </h2>
                  <Link
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-sm font-light inline-flex items-center gap-1 transition-colors"
                    to="/hub/resources"
                  >
                    View All
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-4">
                  {featuredResources.map((resource) => (
                    <MinimalResourceCard
                      key={resource.id}
                      resource={resource}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Shared with You Section */}
            {sharedWithMe && sharedWithMe.length > 0 && (
              <section className="pt-10 border-t border-gray-200 dark:border-slate-700/50">
                <div className="mb-6 flex items-center justify-between">
                  <h2 className="text-base font-normal text-gray-900 dark:text-white">
                    Shared with You
                  </h2>
                  <Link
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-sm font-light inline-flex items-center gap-1 transition-colors"
                    to="/hub/resources?showSharedWithMe=true"
                  >
                    View All
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-4">
                  {sharedWithMe.map((resource) => (
                    <SharedResourceCard
                      key={resource.id}
                      onRemove={refetch}
                      resource={resource}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* User Favorites Section */}
            {favorites && favorites.length > 0 && (
              <section className="pt-10 border-t border-gray-200 dark:border-slate-700/50">
                <div className="mb-6 flex items-center justify-between">
                  <h2 className="text-base font-normal text-gray-900 dark:text-white">
                    Your Favorites
                  </h2>
                  <Link
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-sm font-light inline-flex items-center gap-1 transition-colors"
                    to="/hub/resources?showFavorites=true"
                  >
                    View All
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-4">
                  {favorites.map((resource) => (
                    <MinimalResourceCard
                      key={resource.id}
                      resource={resource}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Empty State */}
            {(!news || news.length === 0) &&
              (!featuredResources || featuredResources.length === 0) &&
              (!favorites || favorites.length === 0) &&
              (!sharedWithMe || sharedWithMe.length === 0) && (
                <div className="text-center py-12">
                  <p className="text-gray-400 dark:text-gray-500">
                    No content available. Check back later for updates tailored
                    to your trade specialization.
                  </p>
                </div>
              )}
          </div>

          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/75 dark:bg-slate-950/75">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HubDashboard;
